// frontend/src/components/booking-management/TripGroupedView.js

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { ChevronDown, ChevronUp, Users, Briefcase, MapPin, Calendar, Clock, User, CheckCircle, AlertCircle, ListOrdered } from 'lucide-react';
import { formatDate, formatTimeString } from '../../utils/dateTimeFormatter';
import { isAdmin } from '../../utils/roleUtils';
import TripAssignmentDialog from './TripAssignmentDialog';
import TripWaypointReorderModal from '../dialogs/TripWaypointReorderModal';
import AppImage from '../common/AppImage';
import { canLaunchWaypointReorder } from '../../utils/waypointReorderAccess';

/**
 * Trip-grouped view component showing trips with expandable booking lists
 * Displays trips with all riders and their booking details
 */
const TripGroupedView = ({
  trips = [],
  userRole,
  onBookingAction,
  onRefreshTrips,
  loading = false,
  preferredDateFormat = 'MM/DD/YYYY',
  preferredTimeFormat = '12h',
  entityId,
  bookingVerificationEnabled = false
}) => {
  const { t } = useTranslation();
  const { background, text, cn } = useTheme();
  const [expandedTrips, setExpandedTrips] = useState(new Set());
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderTrip, setReorderTrip] = useState(null);
  const isAdminUser = isAdmin(userRole);
  const currentTripIds = useMemo(
    () => new Set(trips.map((trip) => String(trip.trip_id))),
    [trips]
  );
  const scopedSelectedTrip = selectedTrip &&
    String(selectedTrip.__selectedEntityId) === String(entityId) &&
    currentTripIds.has(String(selectedTrip.trip_id))
    ? selectedTrip
    : null;
  const scopedReorderTrip = reorderTrip &&
    String(reorderTrip.__selectedEntityId) === String(entityId) &&
    currentTripIds.has(String(reorderTrip.trip_id))
    ? reorderTrip
    : null;

  // Assignment dialog handlers
  const handleOpenAssignmentDialog = (trip) => {
    if (!currentTripIds.has(String(trip?.trip_id))) {
      return;
    }
    console.log('trip IN handleOpenAssignmentDialog', trip);
    setSelectedTrip({ ...trip, __selectedEntityId: entityId });
    setAssignmentDialogOpen(true);
  };

  const handleCloseAssignmentDialog = () => {
    setAssignmentDialogOpen(false);
    setSelectedTrip(null);
  };

  const handleAssignmentSave = async () => {
    // Refresh trip data
    if (onRefreshTrips) {
      await onRefreshTrips();
    }
    handleCloseAssignmentDialog();
  };

  // Normalize booking context for confirmation dialogs triggered from the trip view
  const buildActionDetails = (booking, trip) => ({
    id: booking.booking_id,
    tripId: trip.trip_id,
    userName: booking.user_name,
    routeName: trip.route_name,
    tripDate: trip.trip_date,
    tripTime: trip.departure_time,
    passengers: booking.riders,
    contactNumber: booking.contact_number
  });

  const toggleTripExpand = (tripId) => {
    const newExpanded = new Set(expandedTrips);
    if (newExpanded.has(tripId)) {
      newExpanded.delete(tripId);
    } else {
      newExpanded.add(tripId);
    }
    setExpandedTrips(newExpanded);
  };

  const getTripStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      dispatched: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      in_progress: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      completed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      canceled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  const getBookingStatusColor = (status) => {
    const colors = {
      confirmed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      waitlist: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      canceled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      no_show: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="animate-pulse">
            <div className="h-32 bg-gray-200 rounded-lg dark:bg-gray-700" />
          </div>
        ))}
      </div>
    );
  }

  if (trips.length === 0) {
    return (
      <div className={cn('text-center py-12', text.secondary)}>
        <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p className="text-lg">{t('noBookingsFound')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {trips.map((trip) => {
        const isExpanded = expandedTrips.has(trip.trip_id);
        const hasBookings = trip.bookings && trip.bookings.length > 0;

        return (
          <div
            key={trip.trip_id}
            className={cn(
              'rounded-lg border shadow-sm overflow-hidden',
              background.secondary,
              'border-gray-200 dark:border-gray-700'
            )}
          >
            {/* Trip Header */}
            <div
              className={cn(
                'p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors',
                hasBookings && 'cursor-pointer'
              )}
              onClick={() => hasBookings && toggleTripExpand(trip.trip_id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  {/* Trip Title */}
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className={cn('text-lg font-semibold', text.primary)}>
                      {trip.route_name}
                    </h3>
                    <span className={cn('px-2 py-1 rounded-full text-xs font-semibold', getTripStatusColor(trip.trip_status))}>
                      {t(`tripStatus.${trip.trip_status}`)}
                    </span>
                  </div>
                  {trip.operational_unavailability_warning?.affected && (
                    <div className="mb-3 flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-100">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{t('operationalUnavailability.existingTripWarning')}</span>
                    </div>
                  )}

                  {/* Trip Info Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span className={text.secondary}>{formatDate(trip.trip_date, preferredDateFormat)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-500" />
                      <span className={text.secondary}>{formatTimeString(trip.departure_time, preferredTimeFormat)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-500" />
                      <span className={text.secondary}>
                        {trip.total_riders} {t('riders')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-blue-500" />
                      <span className={text.secondary}>
                        {trip.total_luggages} {t('luggage')}
                      </span>
                    </div>
                  </div>

                  {/* Assignment Status Section - Admin Only */}
                  {isAdminUser && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          {/* Driver Assignment */}
                          <div className="flex items-center gap-2 text-sm">
                            {trip.driver_name ? (
                              <>
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span className="text-gray-700 dark:text-gray-300">
                                  {t('tripAssignments.driverAssigned', { name: trip.driver_name })}
                                </span>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-4 h-4 text-amber-500" />
                                <span className="text-gray-500 dark:text-gray-400">
                                  {t('tripAssignments.driverUnassigned')}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Vehicle Assignment */}
                          <div className="flex items-center gap-2 text-sm">
                            {trip.vehicle_number ? (
                              <>
                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <div className="flex items-center gap-2 flex-wrap">


                                  <span className="text-gray-700 dark:text-gray-300">
                                    {t('tripAssignments.vehicleAssigned', { vehicle: trip.vehicle_number })}
                                    {/* Rider Capacity Display - Shows total and peak */}
                                    {trip.vehicle_capacity > 0 && (
                                      <span className={cn(
                                        "ml-2 px-2 py-0.5 rounded text-xs font-semibold",
                                        // Use peak if available, otherwise fall back to total
                                        (trip.peak_riders ?? trip.total_riders) > trip.vehicle_capacity
                                          ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                          : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                      )}>
                                        {trip.total_riders} {t('riders')} ({t('peak')} {trip.peak_riders ?? trip.total_riders}/{trip.vehicle_capacity})
                                      </span>
                                    )}
                                    {/* Luggage Capacity Display - Shows total and peak */}
                                    {trip.vehicle_luggage_capacity > 0 && (
                                      <span className={cn(
                                        "ml-2 px-2 py-0.5 rounded text-xs font-semibold",
                                        // Use peak if available, otherwise fall back to total
                                        (trip.peak_luggage ?? trip.total_luggages) > trip.vehicle_luggage_capacity
                                          ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                          : (trip.peak_luggage ?? trip.total_luggages) === trip.vehicle_luggage_capacity
                                            ? "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                                            : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                      )}>
                                        {trip.total_luggages} {t('luggage')} ({t('peak')} {trip.peak_luggage ?? trip.total_luggages}/{trip.vehicle_luggage_capacity})
                                      </span>
                                    )}
                                  </span>

                                  {/* Vehicle Picture */}
                                  {trip.vehicle_picture_url && (
                                    <AppImage
                                      src={trip.vehicle_picture_url}
                                      alt={trip.vehicle_number}
                                      crossOrigin="anonymous"
                                      fallbackSrc={null}
                                      className="w-12 h-12 object-cover rounded border border-gray-300 dark:border-gray-600"
                                    />
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-4 h-4 text-amber-500" />
                                <span className="text-gray-500 dark:text-gray-400">
                                  {t('tripAssignments.vehicleUnassigned')}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Reference Route Assignment */}
                          <div className="flex items-center gap-2 text-sm">
                            {trip.selected_archived_type ? (
                              <>
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span className="text-gray-700 dark:text-gray-300">
                                  {t('tripAssignments.routeTypeAssigned', {
                                    type: trip.selected_archived_type === 'representative'
                                          ? t('representativeRoute')
                                          : trip.archived_type_display_name || trip.selected_archived_type
                                  })}
                                </span>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-4 h-4 text-amber-500" />
                                <span className="text-gray-500 dark:text-gray-400">
                                  {t('tripAssignments.routeTypeUnassigned')}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Trip Action Buttons */}
                        <div className="flex gap-2">
                          {canLaunchWaypointReorder({
                            trip,
                            status: trip.trip_status,
                            role: userRole,
                            groupedAdminSurface: true
                          }) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!currentTripIds.has(String(trip.trip_id))) {
                                  return;
                                }
                                setReorderTrip({ ...trip, __selectedEntityId: entityId });
                                setShowReorderModal(true);
                              }}
                              className="px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded transition-colors flex items-center gap-1"
                            >
                              <ListOrdered className="w-4 h-4" />
                              {t('reorderStops')}
                            </button>
                          )}

                          {/* Edit Assignment Button */}
                          {!['in_progress', 'canceled', 'completed'].includes(trip.trip_status) && (
                            <button
                              onClick={() => handleOpenAssignmentDialog(trip)}
                              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                            >
                              {trip.driver_id || trip.vehicle_id || trip.selected_archived_type
                                ? t('tripAssignments.edit')
                                : t('tripAssignments.assign')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Expand Icon */}
                {hasBookings && (
                  <div className="ml-4">
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Expandable Bookings List */}
            {isExpanded && hasBookings && (
              <div className={cn('border-t p-4', 'border-gray-200 dark:border-gray-700')}>
                <h4 className={cn('text-sm font-semibold mb-3', text.primary)}>
                  {t('ridersList')} ({trip.bookings.length})
                </h4>

                <div className="space-y-2">
                  {trip.bookings.map((booking) => (
                    <div
                      key={booking.booking_id}
                      className={cn(
                        'p-3 rounded-md border',
                        background.primary,
                        'border-gray-200 dark:border-gray-600'
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Booking Header */}
                          <div className="flex items-center gap-2 mb-2">
                            <User className="w-4 h-4 text-blue-500" />
                            <span className={cn('font-medium', text.primary)}>
                              {booking.user_name}
                            </span>
                            <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold', getBookingStatusColor(booking.booking_status))}>
                              {t(`bookingStatus.${booking.booking_status}`)}
                            </span>
                          </div>

                          {/* Booking Details Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            <div className="flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-green-500 mt-0.5" />
                              <div>
                                <div className={cn('font-medium', text.secondary)}>
                                  {t('pickup')}:
                                </div>
                                <div className={text.primary}>{booking.pickup_name}</div>
                                <div className={cn('text-xs', text.secondary)}>
                                  {booking.pickup_address}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-red-500 mt-0.5" />
                              <div>
                                <div className={cn('font-medium', text.secondary)}>
                                  {t('dropoff')}:
                                </div>
                                <div className={text.primary}>{booking.dropoff_name}</div>
                                <div className={cn('text-xs', text.secondary)}>
                                  {booking.dropoff_address}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Passengers & Luggage */}
                          <div className="flex gap-4 mt-2 text-sm">
                            <div className={text.secondary}>
                              <Users className="w-4 h-4 inline mr-1" />
                              {booking.riders} {t('riders')}
                            </div>
                            <div className={text.secondary}>
                              <Briefcase className="w-4 h-4 inline mr-1" />
                              {booking.luggages} {t('luggage')}
                            </div>
                          </div>

                          {/* Contact & Special Requests - Admin Only */}
                          {isAdminUser && (
                            <div className="mt-2 text-sm space-y-1">
                              {booking.contact_number && (
                                <div className={text.secondary}>
                                  <span className="font-medium">{t('contact')}:</span> {booking.contact_number}
                                </div>
                              )}
                              {booking.special_requests && (
                                <div className={text.secondary}>
                                  <span className="font-medium">{t('specialRequests')}:</span> {booking.special_requests}
                                </div>
                              )}
                              {bookingVerificationEnabled && booking.entity_verification_value && (
                                <div className={text.secondary}>
                                  <span className="font-medium">{t('verificationInfo')}:</span> {booking.entity_verification_value}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Booking Actions - Admin Only */}
                        {isAdminUser && onBookingAction && (
                          <div className="ml-4 flex flex-col gap-2">
                            {booking.booking_status === 'pending' && (
                              <>
                                <button
                                  onClick={() => onBookingAction && onBookingAction('confirm', buildActionDetails(booking, trip))}
                                  className="px-3 py-1 text-xs rounded-md bg-green-600 text-white hover:bg-green-700"
                                >
                                  {t('confirm')}
                                </button>
                                <button
                                  onClick={() => onBookingAction && onBookingAction('cancel', buildActionDetails(booking, trip))}
                                  className="px-3 py-1 text-xs rounded-md bg-red-600 text-white hover:bg-red-700"
                                >
                                  {t('cancel')}
                                </button>
                              </>
                            )}
                            {booking.booking_status === 'confirmed' && (
                              <button
                                onClick={() => onBookingAction && onBookingAction('cancel', buildActionDetails(booking, trip))}
                                className="px-3 py-1 text-xs rounded-md bg-red-600 text-white hover:bg-red-700"
                              >
                                {t('cancel')}
                              </button>
                            )}
                            {/* Waitlist bookings can be cancelled */}
                            {booking.booking_status === 'waitlist' && (
                              <button
                                onClick={() => onBookingAction && onBookingAction('cancel', buildActionDetails(booking, trip))}
                                className="px-3 py-1 text-xs rounded-md bg-red-600 text-white hover:bg-red-700"
                              >
                                {t('cancel')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Trip Assignment Dialog */}
      {assignmentDialogOpen && scopedSelectedTrip && (
        <TripAssignmentDialog
          isOpen={assignmentDialogOpen}
          onClose={handleCloseAssignmentDialog}
          trip={scopedSelectedTrip}
          entityId={entityId}
          onSave={handleAssignmentSave}
        />
      )}

      {/* Waypoint Reorder Modal */}
      <TripWaypointReorderModal
        isOpen={showReorderModal && Boolean(scopedReorderTrip)}
        onClose={() => {
          setShowReorderModal(false);
          setReorderTrip(null);
        }}
        entityId={entityId}
        tripId={scopedReorderTrip?.trip_id}
        tripInfo={{
          routeId: scopedReorderTrip?.route_id,
          routeName: scopedReorderTrip?.route_name,
          tripDate: scopedReorderTrip?.trip_date,
          departureTime: scopedReorderTrip?.departure_time
        }}
        onOrderChanged={async () => {
          if (onRefreshTrips) await onRefreshTrips();
        }}
      />
    </div>
  );
};

export default TripGroupedView;
